/**
 * Turning a calendar date into an instant, in the agency's own timezone.
 *
 * A licence card carries a DATE — "expires 1 March 2027" — but every expiry the engine reads
 * is a full ISO instant (`mintCredentialEffectSchema` types them `z.string().datetime()`).
 * Something has to bridge the two, and where that bridge lands is not cosmetic.
 *
 * `readiness/evaluate.ts` decides expiry with
 *
 *     remaining = Math.floor((Date.parse(dueAt) - Date.parse(now)) / MS_PER_DAY)
 *     expired when remaining + graceDays < 0
 *
 * At zero grace that is simply `dueAt < now`. So a licence expiring 2027-03-01 stored as UTC
 * midnight is already in the past by 4am in Virginia — the system would report a nurse's
 * licence expired on a day it is still valid, and the nightly scan would chase her a day
 * early, every time, for every person on the roster.
 *
 * The date therefore becomes the LAST instant of that day where the agency operates.
 */
/**
 * Virginia observes US Eastern time.
 *
 * Kept as a constant with `agencySettings.timeZone` as its source of truth, pinned by a test,
 * rather than read from the catalog here: `catalog/index.ts` re-exports the seeder, which
 * value-imports the step registry, so a step type reaching the catalog through this module
 * would close a runtime import cycle. The test is what stops the two drifting.
 */
export const AGENCY_TIME_ZONE = 'America/New_York';
/** `YYYY-MM-DD`, which is what a native date input produces. */
export const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Minutes that `timeZone` is ahead of UTC AT A GIVEN INSTANT.
 *
 * Computed per instant rather than taken as a constant because Eastern is UTC-5 in winter and
 * UTC-4 in summer. A fixed offset is wrong for roughly half the year, and wrong in the
 * direction that expires people early.
 *
 * Rounded to whole minutes: the probe reconstructs the local wall clock to second precision,
 * so a sub-second input would otherwise leak a fractional offset back into the result.
 */
function offsetMinutes(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    }).formatToParts(instant);
    const at = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    // `hour12: false` emits 24 for midnight in some ICU versions; % 24 normalises it.
    const asUtc = Date.UTC(at('year'), at('month') - 1, at('day'), at('hour') % 24, at('minute'), at('second'));
    return Math.round((asUtc - instant.getTime()) / 60000);
}
/**
 * Refuse a date that does not exist.
 *
 * `Date.UTC` normalises overflow silently, so "2027-02-30" became 2 March and "2026-02-29" —
 * a plausible typo for a non-leap year — became 1 March. HR types licence expiry dates off a
 * card, and a mistyped one turning into a real date two days later would be recorded as
 * attested fact with nothing anywhere reporting a problem. Round-tripping is the cheapest
 * way to catch it: if the instant does not fall back on the date asked for, the date was not
 * a date.
 */
function checkRoundTrip(instant, calendarDate, timeZone) {
    if (agencyCalendarDate(instant, timeZone) !== calendarDate) {
        throw new Error(`no such calendar date: "${calendarDate}"`);
    }
    return instant;
}
/**
 * The last millisecond of `calendarDate` in the agency's timezone, as an ISO instant.
 *
 * The offset is resolved twice. The first pass guesses using the offset in force at the naive
 * probe instant; the second re-resolves at the instant that guess produced. They differ only
 * when a DST transition falls between the two.
 *
 * For America/New_York they never do — Eastern switches at 2am local, which is 06:00 or 07:00
 * UTC, and both probes sit outside that window. I checked by deleting the second pass: every
 * test still passed, including both transition days. So it is a guard for a timezone this
 * agency does not currently operate in, not something Eastern exercises. Kept because the
 * function takes a `timeZone` argument and the cost is one formatToParts call, but the comment
 * says what is true rather than implying a bug it prevents here.
 */
export function endOfDayInAgencyZone(calendarDate, timeZone = AGENCY_TIME_ZONE) {
    if (!CALENDAR_DATE.test(calendarDate)) {
        throw new Error(`expected a YYYY-MM-DD calendar date, got "${calendarDate}"`);
    }
    const [year, month, day] = calendarDate.split('-').map(Number);
    const naive = Date.UTC(year, month - 1, day, 23, 59, 59, 999);
    const firstPass = naive - offsetMinutes(new Date(naive), timeZone) * 60000;
    const settled = naive - offsetMinutes(new Date(firstPass), timeZone) * 60000;
    return checkRoundTrip(new Date(settled).toISOString(), calendarDate, timeZone);
}
/**
 * The FIRST instant of `calendarDate`, for a date something was issued on.
 *
 * An issue date is a lower bound — "we have held this since" — so it belongs at the start of its
 * day, where end-of-day would be for an expiry. `fromCredentials` compares the two directly
 * (`expiresAt <= issuedAt` is read as impossible dates), so a card issued and expiring on the
 * same day must not read as impossible.
 */
export function startOfDayInAgencyZone(calendarDate, timeZone = AGENCY_TIME_ZONE) {
    if (!CALENDAR_DATE.test(calendarDate)) {
        throw new Error(`expected a YYYY-MM-DD calendar date, got "${calendarDate}"`);
    }
    const [year, month, day] = calendarDate.split('-').map(Number);
    const naive = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
    const firstPass = naive - offsetMinutes(new Date(naive), timeZone) * 60000;
    const settled = naive - offsetMinutes(new Date(firstPass), timeZone) * 60000;
    return checkRoundTrip(new Date(settled).toISOString(), calendarDate, timeZone);
}
/** The calendar date an instant falls on, in the agency's timezone. For display and for tests. */
export function agencyCalendarDate(instant, timeZone = AGENCY_TIME_ZONE) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date(instant));
}
/** Is this expiry already past? Used to refuse a credential that would land expired. */
export function isPastInAgencyZone(calendarDate, now, timeZone = AGENCY_TIME_ZONE) {
    // Instants, NOT ISO strings. The end of a day carries milliseconds and a caller's `now`
    // often does not, and '.' (0x2E) sorts before 'Z' (0x5A) — so '…59.999Z' < '…59Z' is true
    // as text while being false in time. That flipped the answer for the last second of every
    // day, in the direction that refuses a credential which is still valid.
    return Date.parse(endOfDayInAgencyZone(calendarDate, timeZone)) < Date.parse(now);
}
const MS_PER_DAY = 86_400_000;
/**
 * Whole calendar days from `from` to `to`, counted the way a person reads two dates off a
 * calendar rather than by elapsed hours.
 *
 * Elapsed time would not do. At noon on the 14th, a 5pm deadline on the 17th is 3.2 elapsed days,
 * which rounds either to 4 — more time than there is — or to 3 only by luck of the hour; and a
 * deadline at 5pm TODAY would read as most of a day away when it is hours away. Both instants are
 * taken in the agency's timezone, so the count does not change with the reader's own.
 *
 * 0 means the same day. Negative means `to` is already behind `from`.
 */
export function agencyCalendarDaysBetween(from, to, timeZone = AGENCY_TIME_ZONE) {
    const day = (instant) => Date.parse(`${agencyCalendarDate(instant, timeZone)}T00:00:00Z`);
    return Math.round((day(to) - day(from)) / MS_PER_DAY);
}
/**
 * The start of the agency day `days` calendar days from `from`. Negative counts backwards.
 *
 * CALENDAR DAYS, NOT 24-HOUR BLOCKS, and in `America/New_York` the difference is a real defect
 * rather than a nicety. Adding `days * 86_400_000` to an instant crosses the DST boundaries: seven
 * "days" added in March lands on the EIGHTH calendar day and in November on the sixth, so a
 * correction due date computed that way would have HR chasing a day late in spring and a day early
 * in autumn — exactly the drift `agencyCalendarDaysBetween` exists to prevent on the other side of
 * the comparison.
 *
 * The shift is done on a UTC-midnight anchor, where every day is exactly 24 hours and the
 * arithmetic is total, and the result is then re-anchored to the agency's own start of day. So the
 * answer is a real calendar date in the agency's zone whatever the offset was at either end.
 */
export function addAgencyCalendarDays(from, days, timeZone = AGENCY_TIME_ZONE) {
    const anchor = Date.parse(`${agencyCalendarDate(from, timeZone)}T00:00:00Z`);
    const shifted = new Date(anchor + days * MS_PER_DAY).toISOString().slice(0, 10);
    return startOfDayInAgencyZone(shifted, timeZone);
}
/**
 * What `formatRecordedCalendarDate` returns for a value it cannot read.
 *
 * EXPORTED because callers interpolate its result into a sentence, and a caller that wants to say
 * something else in that case has to recognise it. #396's review found `Hired on Date unavailable`
 * shipped from exactly that: the sentinel read as a date because nothing distinguished it. A caller
 * comparing against its own copy of the string would break silently the day this wording changed.
 */
export const DATE_UNAVAILABLE = 'Date unavailable';
/** Calendar evidence entered as a date stays that date in every reviewer's timezone. */
export function formatRecordedCalendarDate(value, locale = 'en-US') {
    const calendar = value.slice(0, 10);
    if (!CALENDAR_DATE.test(calendar))
        return DATE_UNAVAILABLE;
    const instant = new Date(`${calendar}T12:00:00.000Z`);
    if (!Number.isFinite(instant.getTime()) || instant.toISOString().slice(0, 10) !== calendar)
        return DATE_UNAVAILABLE;
    return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' }).format(instant);
}
//# sourceMappingURL=agency-dates.js.map